// Theme JavaScript with GSAP integration

// HTML-escape any string interpolated into a template literal that ends up
// in innerHTML. Shopify titles can contain quotes/ampersands; never trust
// them inside attribute or text contexts without escaping.
function escapeHtml(value) {
  if (value === null || value === undefined) return '';
  return String(value)
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;')
    .replace(/'/g, '&#39;');
}
window.escapeHtml = escapeHtml;

class ThemeAnimations {
  constructor() {
    this.lenis = (window.__squizziLenis && window.__squizziLenis.lenis) ? window.__squizziLenis.lenis : null;
    // Tracks animations/ScrollTriggers created for the current #swup view so
    // they can be reverted in a single call before re-running on next view.
    this.contentContext = null;
    this.init();
    this.initCustomCursor();
    if (!this.lenis) this.initLenis();
  }

  init() {
    // Wait for GSAP to be available
    this.waitForGSAP(() => {
      this.setupAnimations();
    });
  }

  initLenis() {
    // Wait for Lenis to be available
    this.waitForLenis(() => {
      if (typeof Lenis !== 'undefined') {
        this.lenis = new Lenis({
          duration: 0.7,
          easing: (t) => Math.min(1, 1.001 - Math.pow(2, -10 * t)),
          smoothWheel: true,
          wheelMultiplier: 1,
          touchMultiplier: 1,
          infinite: false,
          anchors: { offset: 0, duration: 1.0 },
        });

        // Get scroll value
        this.lenis.on('scroll', () => {
          // Update GSAP ScrollTrigger with scroll position
          if (typeof ScrollTrigger !== 'undefined') {
            ScrollTrigger.update();
          }
        });

        // Drive Lenis with GSAP's ticker (or fall back to rAF). Guarded so a
        // re-entrant init doesn't stack multiple callbacks per frame.
        if (!window.__squizziLenisDriverAttached) {
          window.__squizziLenisDriverAttached = true;
          if (typeof gsap !== 'undefined') {
            const tickerFn = (time) => {
              const lenis = window.__squizziLenis && window.__squizziLenis.lenis;
              if (lenis) lenis.raf(time * 1000);
            };
            window.__squizziLenisTickerFn = tickerFn;
            gsap.ticker.add(tickerFn);
          } else {
            const raf = (time) => {
              const lenis = window.__squizziLenis && window.__squizziLenis.lenis;
              if (lenis) lenis.raf(time);
              requestAnimationFrame(raf);
            };
            requestAnimationFrame(raf);
          }
        }

        // Store Lenis instance globally for page transitions
        window.__squizziLenis = { lenis: this.lenis };

        // Sync current position to avoid jump on init
        this.lenis.scrollTo(window.scrollY || 0, { immediate: true });

        // Pause Lenis during Swup transitions
        if (window.__swupInstance) {
          window.__swupInstance.hooks.before('animation:start', () => {
            if (this.lenis) {
              this.lenis.stop();
            }
          });

          window.__swupInstance.hooks.on('page:view', () => {
            if (this.lenis) {
              this.lenis.start();
            }
          });
        }
      }
    });
  }

  waitForLenis(callback, attempts = 0) {
    if (typeof Lenis !== 'undefined') {
      callback();
    } else if (attempts < 100) { // ~5s cap
      setTimeout(() => this.waitForLenis(callback, attempts + 1), 50);
    }
  }

  waitForGSAP(callback, attempts = 0) {
    if (typeof gsap !== 'undefined' && typeof ScrollTrigger !== 'undefined') {
      callback();
    } else if (attempts < 100) { // ~5s cap
      setTimeout(() => this.waitForGSAP(callback, attempts + 1), 50);
    }
  }

  setupAnimations() {
    if (typeof ScrollTrigger !== 'undefined') {
      gsap.registerPlugin(ScrollTrigger);
    }

    // One-shot intros: target elements that persist outside #swup so we
    // don't replay them on every Swup navigation.
    this.setupHeaderIntro();
    this.setupPageIntro();
    this.setupFooterHover();

    // Re-runnable animations scoped to the #swup container. Called again
    // from runPageScripts() on every Swup page:view so the new content
    // gets fresh ScrollTriggers.
    this.setupContentAnimations();
  }

  setupHeaderIntro() {
    const nav = document.querySelector('.nav');
    if (!nav) return;
    gsap.fromTo(nav,
      { y: -100, opacity: 0 },
      { y: 0, opacity: 1, duration: 0.6, ease: 'power2.out' }
    );
  }

  setupPageIntro() {
    const mainContent = document.querySelector('main') || document.querySelector('.featured-products');
    if (!mainContent) return;
    gsap.fromTo(mainContent,
      { opacity: 0, y: 30 },
      { opacity: 1, y: 0, duration: 0.8, delay: 0.3, ease: 'power2.out' }
    );
  }

  setupFooterHover() {
    // Footer is outside #swup, so handlers bound once persist across all
    // Swup navigations — no need to re-bind in runPageScripts.
    const footerLinks = document.querySelectorAll('.footer__links a');
    footerLinks.forEach(link => {
      link.addEventListener('mouseenter', () => {
        gsap.to(link, { x: 10, duration: 0.3, ease: 'power2.out' });
      });
      link.addEventListener('mouseleave', () => {
        gsap.to(link, { x: 0, duration: 0.3, ease: 'power2.out' });
      });
    });
  }

  setupContentAnimations() {
    if (typeof gsap === 'undefined' || typeof ScrollTrigger === 'undefined') return;

    // Revert the previous page's animations and their tracked ScrollTriggers.
    // gsap.context() auto-tracks anything created inside its callback, so a
    // single revert() cleans up the whole batch — no manual bookkeeping.
    if (this.contentContext) {
      this.contentContext.revert();
      this.contentContext = null;
    }

    const swupRoot = document.getElementById('swup');
    if (!swupRoot) return;

    this.contentContext = gsap.context(() => {
      this.animateHeadings(swupRoot);
      this.animateParagraphs(swupRoot);
      this.animateFeaturedProducts(swupRoot);
      this.animateNoMess(swupRoot);
    }, swupRoot);
  }

  animateHeadings(root) {
    const headings = root.querySelectorAll('h1, h2, h3, h4, h5, h6');
    headings.forEach((heading) => {
      this.splitText(heading);
      const words = heading.querySelectorAll('.word');
      if (words.length === 0) return;

      const isLargeHeading = heading.style.fontSize && parseInt(heading.style.fontSize) > 80;
      const stagger = isLargeHeading ? 0.15 : 0.1;
      const duration = isLargeHeading ? 1.5 : 1.2;

      gsap.fromTo(words,
        { y: 100, opacity: 0, rotationX: -90 },
        {
          y: 0, opacity: 1, rotationX: 0,
          duration, stagger, ease: 'power3.out',
          scrollTrigger: {
            trigger: heading,
            start: 'top 85%',
            end: 'bottom 15%',
            toggleActions: 'play none none reverse'
          }
        }
      );
    });
  }

  animateParagraphs(root) {
    const paragraphs = root.querySelectorAll('p');
    paragraphs.forEach((paragraph, index) => {
      // Chefs-play paragraphs are revealed by the section's own intro-fade
      // timeline; skip them here so we don't fight that animation.
      if (paragraph.closest('.chefs-play__container')) return;

      gsap.fromTo(paragraph,
        { y: 50, opacity: 0 },
        {
          y: 0, opacity: 1, duration: 1, delay: index * 0.1, ease: 'power2.out',
          scrollTrigger: {
            trigger: paragraph,
            start: 'top 90%',
            end: 'bottom 10%',
            toggleActions: 'play none none reverse'
          }
        }
      );
    });
  }

  animateFeaturedProducts(root) {
    const productCards = root.querySelectorAll('.featured-product-card');
    if (!productCards.length) return;

    gsap.set(productCards, { opacity: 0, y: 50 });
    gsap.to(productCards, {
      opacity: 1, y: 0, duration: 0.8, stagger: 0.2, ease: 'power2.out',
      scrollTrigger: {
        trigger: '.featured-products',
        start: 'top 80%',
        end: 'bottom 20%',
        toggleActions: 'play none none reverse'
      }
    });
  }

  animateNoMess(root) {
    const cookingImage = root.querySelector('.no-mess__cooking-image');
    if (cookingImage) {
      gsap.set(cookingImage, { scale: 1.5, transformOrigin: 'center center' });
      gsap.to(cookingImage, {
        scale: 1, duration: 1, ease: 'power2.out',
        scrollTrigger: {
          trigger: '.no-mess',
          start: 'top 80%',
          end: 'center center',
          toggleActions: 'play none none reverse'
        }
      });
    }

    const noMessCards = root.querySelectorAll('.no-mess__card');
    if (noMessCards.length) {
      gsap.fromTo(noMessCards,
        { y: 50, opacity: 0, scale: 0.8 },
        {
          y: 0, opacity: 1, scale: 1, duration: 0.8, stagger: 0.2, ease: 'power2.out',
          scrollTrigger: {
            trigger: '.no-mess',
            start: 'top 70%',
            end: 'bottom 30%',
            toggleActions: 'play none none reverse'
          }
        }
      );
    }
  }

  splitText(element) {
    // Skip text splitting for elements that contain HTML tags to preserve structure
    if (element.innerHTML.includes('<')) {
      return;
    }

    // Split text into words for word-by-word animation, preserving line breaks
    const text = element.innerHTML;
    const lines = text.split('<br>');
    element.innerHTML = '';

    lines.forEach((line, lineIndex) => {
      if (lineIndex > 0) {
        element.appendChild(document.createElement('br'));
      }

      const words = line.split(' ');
      words.forEach((word) => {
        if (word.trim() !== '') {
          const wordSpan = document.createElement('span');
          wordSpan.textContent = word;
          wordSpan.className = 'word';
          wordSpan.style.display = 'inline-block';
          wordSpan.style.marginRight = '0.25em';
          element.appendChild(wordSpan);
        }
      });
    });
  }

  initCustomCursor() {
    const cursor = document.querySelector('.custom-cursor');
    const defaultCursorUrl = window.cursorAssets?.default;
    const linkCursorUrl = window.cursorAssets?.link;

    if (!cursor) return;

    // Detect touch devices and disable custom cursor
    const isTouchDevice = 'ontouchstart' in window || navigator.maxTouchPoints > 0;
    const prefersReducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    const hasHover = window.matchMedia('(hover: hover)').matches;
    
    if (isTouchDevice || prefersReducedMotion || !hasHover) {
      cursor.style.display = 'none';
      return;
    }

    // Preload cursor assets to avoid flicker on first hover
    if (defaultCursorUrl) { const img = new Image(); img.src = defaultCursorUrl; }
    if (linkCursorUrl) { const img = new Image(); img.src = linkCursorUrl; }

    const imgEl = cursor.querySelector('img');

    // Elements that should trigger the cursor's "hover" state.
    const INTERACTIVE_SELECTOR = 'button, input, textarea, select, [role="button"], [tabindex]';

    let mouseX = 0;
    let mouseY = 0;
    let cursorX = 0;
    let cursorY = 0;
    let rafId = null;

    // Smooth cursor animation using CSS variables so classes can scale separately
    const animateCursor = () => {
      cursorX += (mouseX - cursorX) * 0.6;
      cursorY += (mouseY - cursorY) * 0.6;

      cursor.style.setProperty('--cursor-x', `${cursorX}px`);
      cursor.style.setProperty('--cursor-y', `${cursorY}px`);

      const dx = Math.abs(mouseX - cursorX);
      const dy = Math.abs(mouseY - cursorY);
      if (dx < 0.5 && dy < 0.5) { rafId = null; return; }
      rafId = requestAnimationFrame(animateCursor);
    };

    // Update mouse position; start RAF lazily
    document.addEventListener('mousemove', (e) => {
      mouseX = e.clientX;
      mouseY = e.clientY;
      if (rafId === null) rafId = requestAnimationFrame(animateCursor);
    }, { passive: true });

    // Delegated hover effects to minimize listeners
    document.addEventListener('mouseover', (e) => {
      const target = e.target;
      if (!(target instanceof Element)) return;
      if (target.closest('a')) {
        cursor.classList.add('link');
        if (imgEl && linkCursorUrl) imgEl.src = linkCursorUrl;
        return;
      }
      if (target.closest(INTERACTIVE_SELECTOR)) {
        cursor.classList.add('hover');
      }
    }, { passive: true });

    document.addEventListener('mouseout', (e) => {
      const target = e.target;
      if (!(target instanceof Element)) return;
      if (target.closest('a')) {
        cursor.classList.remove('link');
        if (imgEl && defaultCursorUrl) imgEl.src = defaultCursorUrl;
      }
      if (target.closest(INTERACTIVE_SELECTOR)) {
        cursor.classList.remove('hover');
      }
    }, { passive: true });

    // Hide cursor when leaving window
    document.addEventListener('mouseleave', () => {
      cursor.style.opacity = '0';
    }, { passive: true });

    document.addEventListener('mouseenter', () => {
      cursor.style.opacity = '1';
    }, { passive: true });
  }
}

// Navigation interactions originally defined in sections/header.liquid
class Navigation {
  constructor() {
    this.menuButton = document.querySelector('[data-menu-toggle]');
    this.menuDrawer = document.querySelector('[data-menu-drawer]');
    this.menuCloseButtons = document.querySelectorAll('[data-menu-close]');
    
    this.cartButton = document.querySelector('[data-cart-toggle]');
    this.cartDrawer = document.querySelector('[data-cart-drawer]');
    this.cartCloseButtons = document.querySelectorAll('[data-cart-close]');
    
    this.cartCurrency = undefined;
    this.init();
  }

  init() {
    this.bindEvents();
    this.loadRecommendations();
    this.bindAjaxProductForms();
    this.bindCartDiscountUI();
  }

  // ---- Shared cart helpers -------------------------------------------------
  // The Cart Ajax API (/cart/*.js) is hit from several places with identical
  // fetch + JSON + error-handling boilerplate; these helpers centralise it.

  async fetchCartJson() {
    const res = await fetch('/cart.js');
    if (!res.ok) throw new Error(`/cart.js ${res.status}`);
    return res.json();
  }

  async postCartJson(url, body, errorLabel) {
    try {
      const res = await fetch(url, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(body)
      });
      if (!res.ok) {
        console.error(`${errorLabel}:`, res.status, res.statusText);
        return null;
      }
      return await res.json();
    } catch (error) {
      console.error(`${errorLabel} (network):`, error);
      return null;
    }
  }

  // Add a variant, then refresh the UI from the authoritative full cart.
  // (/cart/add.js returns only the added line item, not the whole cart.)
  async addToCart(variantId, quantity = 1) {
    const added = await this.postCartJson('/cart/add.js', { id: variantId, quantity }, 'Add to cart failed');
    if (!added) return null;
    let cart;
    try {
      cart = await this.fetchCartJson();
    } catch (error) {
      console.error('Failed to fetch cart after add:', error);
      return null;
    }
    this.updateCartDisplay(cart);
    return cart;
  }

  // Post line-item quantity updates and refresh the UI on success.
  async applyCartUpdates(updates) {
    const cart = await this.postCartJson('/cart/update.js', { updates }, 'Cart update failed');
    if (cart) this.updateCartDisplay(cart);
    return cart;
  }

  bindEvents() {
    this.menuButton?.addEventListener('click', () => this.openMenu());
    this.menuCloseButtons.forEach(button => {
      button.addEventListener('click', () => this.closeMenu());
    });

    // Close menu when any link inside the menu drawer is clicked
    this.menuDrawer?.addEventListener('click', (e) => {
      const linkEl = e.target.closest?.('a');
      if (!linkEl || !this.menuDrawer?.contains(linkEl)) return;

      const href = linkEl.getAttribute('href') || '';
      const isHash = href.startsWith('#');
      const isExternal = linkEl.target === '_blank' || (linkEl.host && linkEl.host !== location.host);
      const isModifiedClick = e.button !== 0 || e.metaKey || e.ctrlKey || e.shiftKey || e.altKey;

      // Always close the menu on any anchor click
      this.closeMenu();

      // For standard in-app navigations, delay navigation to allow close transition
      if (!isHash && !isExternal && !isModifiedClick) {
        e.preventDefault();
        const url = linkEl.href;
        setTimeout(() => {
          if (window.__swupInstance && typeof window.__swupInstance.navigate === 'function') {
            window.__swupInstance.navigate(url);
          } else {
            window.location.href = url;
          }
        }, 420); // matches drawer transition timing
      }
    });

    this.cartButton?.addEventListener('click', (e) => {
      // The trigger is an <a href="/cart"> for no-JS fallback; suppress
      // navigation when JS is available so the drawer can open instead.
      // Modifier/middle clicks still navigate (so cmd+click opens in a new tab).
      if (e.button !== 0 || e.metaKey || e.ctrlKey || e.shiftKey || e.altKey) return;
      e.preventDefault();
      this.openCart();
    });
    this.cartCloseButtons.forEach(button => {
      button.addEventListener('click', () => this.closeCart());
    });

    this.bindCartEvents();

    document.addEventListener('keydown', (e) => {
      if (e.key === 'Escape') {
        this.closeMenu();
        this.closeCart();
      }
    });
  }

  bindCartEvents() {
    document.addEventListener('click', (e) => {
      const plusBtn = e.target.closest?.('[data-quantity-plus]');
      if (plusBtn) {
        this.updateQuantity(plusBtn.dataset.itemKey, 1);
      }
      const minusBtn = e.target.closest?.('[data-quantity-minus]');
      if (minusBtn) {
        this.updateQuantity(minusBtn.dataset.itemKey, -1);
      }
      const removeBtn = e.target.closest?.('[data-remove-item]');
      if (removeBtn) {
        this.removeItem(removeBtn.dataset.itemKey);
      }
      const recommendBtn = e.target.closest?.('[data-add-recommendation]');
      if (recommendBtn) {
        this.addRecommendationToCart(recommendBtn.dataset.variantId);
      }
    });

    document.addEventListener('change', (e) => {
      if (e.target.matches?.('.quantity-input')) {
        const itemKey = e.target.dataset.itemKey;
        const newQuantity = parseInt(e.target.value) || 0;
        this.updateQuantityDirect(itemKey, newQuantity);
      }
    });
  }

  bindAjaxProductForms() {
    // Intercept product form submissions and add via AJAX so the cart drawer
    // opens instead of redirecting to /cart. Covers the hero/landing form and
    // the product page form; other contexts fall through to default submit.
    document.addEventListener('submit', async (e) => {
      const form = e.target;
      if (!(form instanceof HTMLFormElement)) return;
      const hasVariantInput = form.querySelector('input[name="id"], select[name="id"]');
      const isProductForm = form.getAttribute('action')?.includes('/cart/add') || !!hasVariantInput;
      if (!isProductForm) return;

      const isHeroContext = !!form.closest('.hero-form-panel');
      const isProductPageContext = !!form.closest('.product-form');
      if (!isHeroContext && !isProductPageContext) return;

      e.preventDefault();

      try {
        // Extract variant id and quantity
        const variantField = form.querySelector('input[name="id"], select[name="id"]');
        const quantityField = form.querySelector('input[name="quantity"], .hero-quantity-hidden');
        const variantId = variantField ? (variantField instanceof HTMLInputElement || variantField instanceof HTMLSelectElement ? variantField.value : '') : '';
        let quantityStr = '1';
        if (quantityField) {
          if (quantityField instanceof HTMLInputElement) {
            quantityStr = quantityField.value || '1';
          } else if ('value' in quantityField) {
            // Fallback for elements exposing a value property
            // @ts-ignore - runtime check only
            quantityStr = quantityField.value || '1';
          }
        }
        const quantity = Math.max(1, parseInt(quantityStr || '1', 10));

        if (!variantId) return;

        const cart = await this.addToCart(variantId, quantity);
        if (cart) this.openCart();
      } catch (err) {
        console.error('Error during AJAX add-to-cart', err);
      }
    });
  }

  async loadRecommendations(cart) {
    try {
      // Reuse the caller's cart when available to avoid a redundant /cart.js
      // fetch on every cart mutation.
      if (!cart) cart = await this.fetchCartJson();
      this.cartCurrency = cart.currency;

      // The catalogue rarely changes within a session; fetch it once and cache.
      if (!this._catalogue) {
        const productsResponse = await fetch('/products.json?limit=8');
        const productsData = await productsResponse.json();
        this._catalogue = productsData.products || [];
      }

      const cartProductIds = cart.items.map(item => item.product_id);
      const recommendations = this._catalogue
        .filter(product => !cartProductIds.includes(product.id))
        .slice(0, 3);
      this.renderRecommendations(recommendations);
    } catch (error) {
      console.error('Error loading recommendations:', error);
    }
  }

  renderRecommendations(products) {
    const recommendationsContainer = document.querySelector('[data-recommendations]');
    const recommendationsSection = document.querySelector('.cart-drawer__recommendations');
    
    if (!recommendationsContainer || products.length === 0) {
      if (recommendationsSection) {
        recommendationsSection.style.display = 'none';
      }
      return;
    }

    if (recommendationsSection) {
      recommendationsSection.style.display = 'block';
    }

    const recommendationsHTML = products.map(product => {
      const firstVariant = product.variants[0];
      const image = product.images[0] || null;
      const title = escapeHtml(product.title);
      const variantId = escapeHtml(firstVariant.id);
      const imgSrc = image ? escapeHtml(image.src) : '';

      return `
        <div class="recommendation-item">
          <div class="recommendation-item__image">
            ${image ? `<img src="${imgSrc}" alt="${title}" width="60" height="60">` : `
              <div class="recommendation-item__placeholder">
                <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1">
                  <rect x="3" y="3" width="18" height="18" rx="2" ry="2"></rect>
                  <circle cx="8.5" cy="8.5" r="1.5"></circle>
                  <polyline points="21,15 16,10 5,21"></polyline>
                </svg>
              </div>
            `}
          </div>
          <div class="recommendation-item__details">
            <h4 class="recommendation-item__title">${title}</h4>
            <p class="recommendation-item__price">${this.formatMoney(firstVariant.price * 100)}</p>
            <button class="recommendation-item__button" data-add-recommendation data-variant-id="${variantId}">
              Add to Cart
            </button>
          </div>
        </div>
      `;
    }).join('');

    recommendationsContainer.innerHTML = recommendationsHTML;
  }

  addRecommendationToCart(variantId) {
    // Drawer is already open here; addToCart refreshes its contents.
    return this.addToCart(variantId, 1);
  }

  // Render the drawer's line items + footer from a cart object.
  renderCartDrawer(cart) {
    const cartItems = document.querySelector('[data-cart-items]');
    if (cartItems) {
      cartItems.innerHTML = cart.item_count > 0
        ? this.renderCartItems(cart.items)
        : '<div class="cart-drawer__empty"><p>Your cart is empty</p></div>';
    }

    const cartFooter = document.querySelector('.cart-drawer__footer');
    if (cartFooter && cart.item_count > 0) {
      cartFooter.innerHTML = this.renderCartFooter(cart);
      // Footer markup is replaced wholesale, so re-bind its discount controls.
      this.bindCartDiscountUI();
    }
  }

  updateCartDisplay(cart) {
    const cartCount = document.querySelector('.nav__cart-count');
    if (cartCount) {
      if (cart.item_count > 0) {
        cartCount.textContent = cart.item_count;
        cartCount.style.display = 'flex';
      } else {
        cartCount.style.display = 'none';
      }
    }

    this.renderCartDrawer(cart);

    const cartPageItems = document.querySelector('.cart-page__items');
    if (cartPageItems) {
      if (cart.item_count > 0) {
        cartPageItems.innerHTML = this.renderCartItems(cart.items);
      } else {
        window.location.href = '/cart';
      }
    }

    this.updateCartPageSummary(cart);
    this.loadRecommendations(cart);
  }

  renderCartItems(items) {
    return items.map(item => {
      const title = escapeHtml(item.title);
      const variantTitle = escapeHtml(item.variant_title);
      const imgSrc = escapeHtml(item.image);
      const key = escapeHtml(item.key);
      return `
      <div class="cart-item">
        <div class="cart-item__image">
          ${item.image ? `<img src="${imgSrc}" alt="${title}" width="60" height="60">` : `
            <div class="cart-item__placeholder">
              <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1">
                <rect x="3" y="3" width="18" height="18" rx="2" ry="2"></rect>
                <circle cx="8.5" cy="8.5" r="1.5"></circle>
                <polyline points="21,15 16,10 5,21"></polyline>
              </svg>
            </div>
          `}
        </div>
        <div class="cart-item__details">
          <div class="cart-item__info">
            <h3 class="cart-item__title">${title}</h3>
            ${item.variant_title && item.variant_title !== 'Default Title' ? `<p class="cart-item__variant">${variantTitle}</p>` : ''}
            <div class="cart-item__price-quantity">
              <div class="cart-item__price">
                ${item.original_line_price > item.final_line_price ? `
                  <span class="cart-item__price--final">${this.formatMoney(item.final_line_price)}</span>
                  <span class="cart-item__price--original">${this.formatMoney(item.original_line_price)}</span>
                ` : `
                  <span class="cart-item__price--final">${this.formatMoney(item.final_line_price)}</span>
                `}
              </div>

              <div class="cart-item__quantity">
                <button class="cart-item__quantity-btn" data-quantity-minus data-item-key="${key}">-</button>
                <span class="cart-item__quantity-text">${item.quantity}</span>
                <button class="cart-item__quantity-btn" data-quantity-plus data-item-key="${key}">+</button>
              </div>
            </div>
          </div>
          <div class="cart-item__controls">
            <button class="cart-item__remove" data-remove-item data-item-key="${key}" aria-label="Remove item">
              <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
                <path d="M3 6h18"></path>
                <path d="M19 6v14c0 1-1 2-2 2H7c-1 0-2-1-2-2V6"></path>
                <path d="M8 6V4c0-1 1-2 2-2h4c1 0 2 1 2 2v2"></path>
              </svg>
            </button>
          </div>
        </div>
      </div>
    `;
    }).join('');
  }

  renderCartFooter(cart) {
    return `
      <div class="cart-drawer__discount">
        <label for="cart-discount-input" class="cart-drawer__discount-label">Discount code</label>
        <div class="cart-drawer__discount-row">
          <input type="text" id="cart-discount-input" class="cart-drawer__discount-input" placeholder="e.g. SAVE10" data-cart-discount-input>
          <button type="button" class="cart-drawer__discount-apply" data-cart-discount-apply>Apply</button>
        </div>
        <p class="cart-drawer__discount-applied" data-cart-discount-applied hidden>Applied: <span data-cart-discount-code></span></p>
      </div>
      <div class="cart-drawer__total">
        <span>Subtotal (${cart.item_count} ${cart.item_count === 1 ? 'item' : 'items'})</span>
        <div class="cart-drawer__total-price">
          ${cart.total_discount > 0 ? `
            <span class="cart-drawer__total--final">${this.formatMoney(cart.total_price)}</span>
            <span class="cart-drawer__total--original">${this.formatMoney(cart.items_subtotal_price)}</span>
          ` : `
            <span class="cart-drawer__total--final">${this.formatMoney(cart.total_price)}</span>
          `}
        </div>
      </div>
      <button class="cart-drawer__checkout" data-cart-checkout>
        GO TO CHECKOUT
      </button>
    `;
  }

  bindCartDiscountUI() {
    const input = document.querySelector('[data-cart-discount-input]');
    const applyBtn = document.querySelector('[data-cart-discount-apply]');
    const appliedWrap = document.querySelector('[data-cart-discount-applied]');
    const appliedCodeEl = document.querySelector('[data-cart-discount-code]');
    const checkoutBtn = document.querySelector('[data-cart-checkout]');

    if (!applyBtn || !checkoutBtn) return;

    const applyCode = () => {
      const code = (input && 'value' in input) ? (input).value.trim() : '';
      if (!code) {
        // navigate to standard checkout
        window.location.href = '/checkout';
        return;
      }
      // Redirect to checkout prefilled with discount
      window.location.href = `/checkout?discount=${encodeURIComponent(code)}`;
    };

    applyBtn.addEventListener('click', applyCode);
    input?.addEventListener('keydown', (e) => {
      if (e.key === 'Enter') {
        e.preventDefault();
        applyCode();
      }
    });
    checkoutBtn.addEventListener('click', (e) => {
      e.preventDefault();
      applyCode();
    });
  }

  updateCartPageSummary(cart) {
    const subtotalElement = document.querySelector('.cart-summary__row:first-child span:last-child');
    if (subtotalElement) {
      subtotalElement.textContent = this.formatMoney(cart.items_subtotal_price);
    }

    const totalElement = document.querySelector('.cart-summary__row--total span:last-child');
    if (totalElement) {
      totalElement.textContent = this.formatMoney(cart.total_price);
    }

    const discountRow = document.querySelector('.cart-summary__row--discount');
    if (cart.total_discount > 0) {
      if (!discountRow) {
        const subtotalRow = document.querySelector('.cart-summary__row:first-child');
        if (subtotalRow) {
          const discountHTML = `
            <div class="cart-summary__row cart-summary__row--discount">
              <span>Discount</span>
              <span>-${this.formatMoney(cart.total_discount)}</span>
            </div>
          `;
          subtotalRow.insertAdjacentHTML('afterend', discountHTML);
        }
      } else {
        const discountAmount = discountRow.querySelector('span:last-child');
        if (discountAmount) {
          discountAmount.textContent = `-${this.formatMoney(cart.total_discount)}`;
        }
      }
    } else if (discountRow) {
      discountRow.remove();
    }
  }

  // Adjust a line item's quantity by a relative delta (needs current qty).
  async updateQuantity(itemKey, change) {
    try {
      const currentCart = await this.fetchCartJson();
      const item = currentCart.items.find(item => item.key === itemKey);
      if (!item) return;
      await this.applyCartUpdates({ [itemKey]: Math.max(0, item.quantity + change) });
    } catch (error) {
      console.error('Error updating cart:', error);
    }
  }

  // Set a line item to an absolute quantity (from the quantity input).
  updateQuantityDirect(itemKey, newQuantity) {
    return this.applyCartUpdates({ [itemKey]: Math.max(0, newQuantity) });
  }

  removeItem(itemKey) {
    return this.applyCartUpdates({ [itemKey]: 0 });
  }

  openMenu() {
    this.menuDrawer?.classList.add('is-open');
    document.body.style.overflow = 'hidden';
    document.body.classList.add('menu-open');
  }

  closeMenu() {
    this.menuDrawer?.classList.remove('is-open');
    // Slide content back immediately
    document.body.classList.remove('menu-open');
    // Restore scrolling after drawer transition completes (matches 400ms CSS)
    setTimeout(() => {
      if (!this.menuDrawer?.classList.contains('is-open')) {
        document.body.style.overflow = '';
      }
    }, 400);
  }

  openCart() {
    this.cartDrawer?.classList.add('is-open');
    document.body.style.overflow = 'hidden';
    document.body.classList.add('cart-open');
  }

  closeCart() {
    this.cartDrawer?.classList.remove('is-open');
    // Slide content back immediately
    document.body.classList.remove('cart-open');
    // Restore scrolling after drawer transition completes (matches 300ms CSS)
    setTimeout(() => {
      if (!this.cartDrawer?.classList.contains('is-open')) {
        document.body.style.overflow = '';
      }
    }, 300);
  }

  formatMoney(cents) {
    let currency = 'USD';
    
    const currencyMeta = document.querySelector('meta[property="og:price:currency"]');
    if (currencyMeta && currencyMeta.content) {
      currency = currencyMeta.content;
    } else if (this.cartCurrency) {
      currency = this.cartCurrency;
    }
    
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: currency
    }).format(cents / 100);
  }
}

// Hide/show header logo based on scroll direction
class HeaderLogoScrollToggle {
  constructor() {
    this.logo = document.querySelector('.nav__logo');
    if (!this.logo) return;

    this.lastScrollY = window.scrollY || 0;
    this.threshold = 4; // ignore tiny scrolls to reduce jitter

    this.onWindowScroll = this.onWindowScroll.bind(this);
    this.attach();
  }

  attach() {
    // Fallback to window scroll; works with Lenis using window as scroller
    window.addEventListener('scroll', this.onWindowScroll, { passive: true });
  }

  onWindowScroll() {
    const currentY = window.scrollY || 0;
    const delta = currentY - this.lastScrollY;

    if (Math.abs(delta) <= this.threshold) return;

    if (currentY <= 0) {
      this.logo.classList.remove('is-hidden');
      this.lastScrollY = currentY;
      return;
    }

    // Hide on scroll down, show on scroll up
    if (delta > 0) {
      this.logo.classList.add('is-hidden');
    } else {
      this.logo.classList.remove('is-hidden');
    }

    this.lastScrollY = currentY;
  }
}

// Initialize theme animations when DOM is loaded
document.addEventListener('DOMContentLoaded', () => {
  // Guard against multiple initializations during Swup navigations
  if (!window.__squizziInit) {
    window.__squizziInit = true;
    new Navigation();
    new ModalManager();
    // Initialize header logo scroll visibility controller
    window.__headerLogoToggle = new HeaderLogoScrollToggle();
    
    // Initialize Lenis on first load only
    if (!window.__squizziLenis) {
      const themeAnimations = new ThemeAnimations();
      window.__squizziLenisInstance = themeAnimations;
    }
  }
  // Always run page-level animations for current view
  window.runPageScripts();
});

// Refresh ScrollTrigger after each Swup page view. We listen for the
// custom 'swup:page:view' event (dispatched by layout/theme.liquid) rather
// than registering a Swup hook directly — defer-script ordering means
// theme.js parses before window.__swupInstance exists, so the previous
// `if (window.__swupInstance)` check was always false and the refresh
// never fired. Without this, freshly-created ScrollTriggers on the new
// page evaluate against stale measurements and stay dormant.
window.addEventListener('swup:page:view', () => {
  if (typeof ScrollTrigger !== 'undefined') {
    ScrollTrigger.refresh();
  }
});

/*
 * Hero details modal controller.
 *
 * Uses the native <dialog> element with showModal()/close(). The dialog
 * renders in the browser's top layer, which means it ignores ancestor
 * transforms/filters/will-change — critical here because Swup applies
 * transform to .transition-main during page transitions and GSAP can
 * promote ancestors with will-change, both of which break position:fixed.
 *
 * Native dialog also gives us: Escape-to-close, focus trap, inert
 * background, and ::backdrop styling — so the controller stays small.
 */
class ModalManager {
  constructor() {
    if (ModalManager._installed) return;
    ModalManager._installed = true;
    this.init();
  }

  init() {
    document.addEventListener('click', (event) => {
      const openBtn = event.target.closest('[data-details-open]');
      if (openBtn) {
        event.preventDefault();
        event.stopPropagation();
        const controls = openBtn.getAttribute('aria-controls');
        const modal = controls ? document.getElementById(controls) : null;
        if (modal && typeof modal.showModal === 'function' && !modal.open) {
          modal.showModal();
        }
        return;
      }

      const closeBtn = event.target.closest('[data-details-close]');
      if (closeBtn) {
        event.preventDefault();
        const modal = closeBtn.closest('dialog.hero-details-modal');
        if (modal && modal.open) modal.close();
        return;
      }

      // Click on the backdrop (outside the inner dialog) closes the modal
      const openDialog = event.target.closest('dialog.hero-details-modal');
      if (openDialog && event.target === openDialog) {
        openDialog.close();
      }
    });
  }
}

// Expose re-runnable page scripts for Swup
window.runPageScripts = () => {
  try {
    // ModalManager is idempotent (guarded by _installed) — safe to call again.
    new ModalManager();

    // Re-run content animations against the new #swup DOM. The instance was
    // built once on first load; here we just ask it to revert the previous
    // page's context and create a fresh one for the new content.
    const instance = window.__squizziLenisInstance;
    if (instance && typeof instance.setupContentAnimations === 'function') {
      instance.setupContentAnimations();
    }

    // Force ScrollTriggers (both theme.js's and the inline section scripts')
    // to recompute against the new layout. Without this, triggers created
    // during content:replace evaluate against the previous page's measurements
    // and above-the-fold animations stay dormant.
    if (typeof ScrollTrigger !== 'undefined') {
      ScrollTrigger.refresh();
    }
  } catch (e) {
    console.error('Failed to run page scripts', e);
  }
};